import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Stack,
  Tabs,
  Tab,
  Chip,
  FormControlLabel,
  Switch,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { 
  Delete, 
  Edit, 
  Add, 
  Search, 
  FilterList, 
  ArrowBack,
  LocalOffer,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListDealsOfTheDayQuery,
  useAddDealMutation,
  useUpdateDealMutation,
  useDeleteDealOfTheDayMutation,
} from '../../features/admin/dealoftheday.ts';

import {
  useListProductsQuery,
  Product,
} from '../../features/admin/productApi.ts';

interface DealOfTheDay {
  deal_id: number;  // Backend always returns deal_id
  product_id: string | number;
  status?: number;  // For internal use
  deal_status: number;  // This is what backend returns
  discount: number;
  start_date: string;
  end_date: string;
  current_status?: string; // From backend: 'ongoing' | 'upcoming' | 'expired'
}

interface EnrichedDeal extends DealOfTheDay {
  product_name?: string;
  product_image?: string;
  prod_name?: string;
  product_main_image?: string;
  original_price?: number;
  currency?: string;
  sale_price?: number;
  display_status?: number; // Calculated display status
  is_currently_active?: boolean;
}

interface DealFormData {
  deal_id?: number;  // For editing
  product_id: string;
  status: number;
  discount: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
}

const DealOfTheDay = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error'
  });
  const [showForm, setShowForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [enrichedDeals, setEnrichedDeals] = useState<EnrichedDeal[]>([]);
  const [filteredDeals, setFilteredDeals] = useState<EnrichedDeal[]>([]);
  const [formData, setFormData] = useState<DealFormData>({
    product_id: '',
    status: 1,
    discount: '',
    start_date: '',
    end_date: '',
    start_time: '00:00',
    end_time: '23:59',
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof DealFormData, string>>>({});

  // API hooks
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListDealsOfTheDayQuery({});

  // Fetch all products to enrich deals with product details
  const {
    data: allProductsResponse,
    isLoading: isLoadingAllProducts,
  } = useListProductsQuery({
    page: 1,
    limit: 1000,
    search_str: ''
  });

  const [addDeal, { isLoading: isAdding }] = useAddDealMutation();
  const [updateDeal, { isLoading: isUpdating }] = useUpdateDealMutation();
  const [deleteDeal, { isLoading: isDeleting }] = useDeleteDealOfTheDayMutation();

  const isSaving = isAdding || isUpdating;
  const isEditing = Boolean(formData.deal_id);

  // Handle API response structure
  const deals = data?.data || [];
  const allProducts = allProductsResponse?.result || allProductsResponse?.data || [];

  const dealsPerPage = 10;
  const totalRecords = filteredDeals.length;
  const totalPages = Math.ceil(totalRecords / dealsPerPage);

  // Paginate deals
  const paginatedDeals = filteredDeals.slice(
    (page - 1) * dealsPerPage,
    page * dealsPerPage
  );

  /**
   * Calculate the final display status based on:
   * 1. Manual status (deal_status field from backend) - 0 = manually disabled, 1 = manually enabled
   * 2. Date range status (current_status) - 'ongoing', 'upcoming', 'expired'
   * 
   * Logic:
   * - If manually disabled (deal_status = 0): Always show INACTIVE
   * - If manually enabled (deal_status = 1):
   *   - If current_status = 'ongoing': Show ACTIVE
   *   - If current_status = 'upcoming' or 'expired': Show INACTIVE
   */
  const getDisplayStatus = (deal: DealOfTheDay): number => {
    // Use deal_status from backend
    const manualStatus = deal.deal_status;
    
    // If manually disabled, always inactive
    if (manualStatus === 0) {
      return 0;
    }
    
    // If manually enabled, check the date-based status
    if (manualStatus === 1) {
      // If backend provided current_status, use it
      if (deal.current_status) {
        return deal.current_status === 'ongoing' ? 1 : 0;
      }
      
      // Fallback: calculate from dates if current_status not available
      const now = new Date();
      const startDate = new Date(deal.start_date);
      const endDate = new Date(deal.end_date);
      
      // Check if current time is within the deal period
      if (now >= startDate && now <= endDate) {
        return 1; // Active
      } else {
        return 0; // Inactive (upcoming or expired)
      }
    }
    
    return 0; // Default to inactive
  };

  /**
   * Check if deal is currently within its active date range
   */
  const isDealCurrentlyActive = (deal: DealOfTheDay): boolean => {
    if (deal.current_status) {
      return deal.current_status === 'ongoing';
    }
    
    const now = new Date();
    const startDate = new Date(deal.start_date);
    const endDate = new Date(deal.end_date);
    
    return now >= startDate && now <= endDate;
  };

  // Function to parse datetime string and extract date and time
  const parseDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return { date: '', time: '00:00' };
    
    try {
      const dateObj = new Date(dateTimeString);
      const date = dateObj.toISOString().split('T')[0];
      const hours = dateObj.getHours().toString().padStart(2, '0');
      const minutes = dateObj.getMinutes().toString().padStart(2, '0');
      const time = `${hours}:${minutes}`;
      
      return { date, time };
    } catch (error) {
      console.error('Error parsing datetime:', error);
      return { date: '', time: '00:00' };
    }
  };

  // Function to combine date and time into datetime string
  const combineDateTime = (date: string, time: string): string => {
    if (!date) return '';
    
    // If time is not provided, use default
    const actualTime = time || '00:00';
    
    // Format: YYYY-MM-DD HH:MM:SS
    return `${date} ${actualTime}:00`;
  };

  // Enrich deals with product details and calculate status
  useEffect(() => {
    if (deals.length > 0 && allProducts.length > 0) {
      const enriched = deals.map((deal: DealOfTheDay) => {
        const productId = typeof deal.product_id === 'string' ? parseInt(deal.product_id) : deal.product_id;
        const product = allProducts.find((p: Product) => 
          (p.product_id === productId || p.id === productId)
        );

        const originalPrice = product?.product_price || 0;
        const discountAmount = (originalPrice * deal.discount) / 100;
        const salePrice = originalPrice - discountAmount;

        // Calculate display status using the corrected logic
        const displayStatus = getDisplayStatus(deal);
        const isCurrentlyActive = isDealCurrentlyActive(deal);

        return {
          ...deal,
          product_name: product?.prod_name || 'Unknown Product',
          product_image: product?.product_main_image || '',
          prod_name: product?.prod_name || '',
          product_main_image: product?.product_main_image || '',
          original_price: originalPrice,
          currency: product?.currency || 'AED',
          sale_price: salePrice,
          display_status: displayStatus,
          is_currently_active: isCurrentlyActive,
        };
      });
      setEnrichedDeals(enriched);
    } else if (deals.length === 0) {
      setEnrichedDeals([]);
      setFilteredDeals([]);
    }
  }, [deals, allProducts]);

  // Filter deals based on search and tab
  useEffect(() => {
    if (enrichedDeals.length > 0) {
      let filtered = [...enrichedDeals];
      
      // Apply search filter
      if (searchStr.trim()) {
        const searchLower = searchStr.toLowerCase();
        filtered = filtered.filter((deal: EnrichedDeal) => {
          return (
            (deal.product_name?.toLowerCase().includes(searchLower)) ||
            (deal.prod_name?.toLowerCase().includes(searchLower)) ||
            (deal.product_id?.toString().includes(searchLower)) ||
            (deal.deal_id?.toString().includes(searchLower)) ||
            (deal.id?.toString().includes(searchLower)) ||
            (deal.discount?.toString().includes(searchLower)) ||
            (deal.original_price?.toString().includes(searchLower)) ||
            (deal.sale_price?.toString().includes(searchLower))
          );
        });
      }
      
      // Apply tab filter
      if (tabValue === 1) { // Active tab
        filtered = filtered.filter((deal: EnrichedDeal) => {
          const displayStatus = deal.display_status !== undefined ? deal.display_status : getDisplayStatus(deal);
          return displayStatus === 1;
        });
      } else if (tabValue === 2) { // Inactive tab
        filtered = filtered.filter((deal: EnrichedDeal) => {
          const displayStatus = deal.display_status !== undefined ? deal.display_status : getDisplayStatus(deal);
          return displayStatus === 0;
        });
      }
      
      setFilteredDeals(filtered);
    } else {
      setFilteredDeals([]);
    }
  }, [enrichedDeals, searchStr, tabValue]);

  // Reset to first page when tab changes
  useEffect(() => {
    setPage(1);
  }, [tabValue, searchStr]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
  };

  const resetForm = () => {
    setFormData({
      product_id: '',
      status: 1,
      discount: '',
      start_date: '',
      end_date: '',
      start_time: '00:00',
      end_time: '23:59',
    });
    setSelectedProduct(null);
    setFormErrors({});
  };

  const handleAddDeal = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditDeal = (deal: EnrichedDeal) => {
    const product = allProducts.find((p: Product) => 
      (p.product_id === deal.product_id || p.id === deal.product_id)
    );
    
    setSelectedProduct(product || null);
    
    // Parse dates for form
    const startDateTime = parseDateTime(deal.start_date);
    const endDateTime = parseDateTime(deal.end_date);
    
    console.log('Edit Deal - Raw Deal Data:', deal);
    console.log('Edit Deal - deal_id:', deal.deal_id);
    console.log('Edit Deal - deal_status:', deal.deal_status);
    
    setFormData({
      deal_id: deal.deal_id,
      product_id: deal.product_id.toString(),
      status: deal.deal_status,
      discount: deal.discount.toString(),
      start_date: startDateTime.date,
      end_date: endDateTime.date,
      start_time: startDateTime.time,
      end_time: endDateTime.time,
    });
    
    console.log('Edit Deal - Form Data Set:', {
      deal_id: deal.deal_id,
      product_id: deal.product_id.toString(),
      status: deal.deal_status,
      discount: deal.discount.toString(),
      start_date: startDateTime.date,
      end_date: endDateTime.date,
      start_time: startDateTime.time,
      end_time: endDateTime.time,
    });
    
    setFormErrors({});
    setShowForm(true);
  };

  const handleBackToList = () => {
    setShowForm(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name as keyof DealFormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      status: e.target.checked ? 1 : 0
    }));
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof DealFormData, string>> = {};

    if (!formData.product_id || !formData.product_id.trim()) {
      errors.product_id = 'Product is required';
    }
    
    if (!formData.discount || parseFloat(formData.discount) <= 0 || parseFloat(formData.discount) > 100) {
      errors.discount = 'Discount must be between 1 and 100';
    }
    
    if (!formData.start_date) {
      errors.start_date = 'Start date is required';
    }
    
    if (!formData.end_date) {
      errors.end_date = 'End date is required';
    }
    
    if (formData.start_date && formData.end_date) {
      // Combine date and time for validation
      const startDateTime = new Date(combineDateTime(formData.start_date, formData.start_time));
      const endDateTime = new Date(combineDateTime(formData.end_date, formData.end_time));
      
      if (endDateTime <= startDateTime) {
        errors.end_date = 'End date must be after start date';
      }
    }

    // Additional validation for edit mode
    if (isEditing && !formData.deal_id) {
      console.error('Edit mode but no deal_id found:', formData);
      setSnackbar({
        open: true,
        message: 'Error: Deal ID is missing. Cannot update.',
        severity: 'error'
      });
      return false;
    }

    setFormErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    
    if (!isValid) {
      console.error('Validation errors:', errors);
    }
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form submitted with data:', formData);
    console.log('isEditing:', isEditing);
    
    if (!validateForm()) {
      console.error('Validation failed');
      return;
    }

    if (!selectedProduct && !isEditing) {
      setSnackbar({
        open: true,
        message: 'Please select a product',
        severity: 'error'
      });
      return;
    }

    try {
      // Format dates for API
      const startDateTime = combineDateTime(formData.start_date, formData.start_time);
      const endDateTime = combineDateTime(formData.end_date, formData.end_time);
      
      if (isEditing && formData.deal_id) {
        // Backend expects these exact fields for update
        const updatePayload = {
          deal_id: Number(formData.deal_id),
          product_id: Number(formData.product_id),
          start_date: startDateTime,
          end_date: endDateTime,
          discount: Number(formData.discount),
          status: Number(formData.status),
        };
        
        console.log('UPDATE - Sending payload:', updatePayload);
        console.log('UPDATE - Field check:');
        console.log('  deal_id:', updatePayload.deal_id, 'exists:', !!updatePayload.deal_id);
        console.log('  product_id:', updatePayload.product_id, 'exists:', !!updatePayload.product_id);
        console.log('  start_date:', updatePayload.start_date, 'exists:', !!updatePayload.start_date);
        console.log('  end_date:', updatePayload.end_date, 'exists:', !!updatePayload.end_date);
        console.log('  discount:', updatePayload.discount, 'exists:', updatePayload.discount !== undefined);
        console.log('  status:', updatePayload.status, 'exists:', updatePayload.status !== undefined);
        
        await updateDeal(updatePayload).unwrap();
        setSnackbar({
          open: true,
          message: 'Deal updated successfully!',
          severity: 'success'
        });
      } else {
        // Add new deal
        const addPayload = {
          product_id: Number(formData.product_id),
          start_date: startDateTime,
          end_date: endDateTime,
          discount: Number(formData.discount),
          status: Number(formData.status),
        };
        
        console.log('ADD - Sending payload:', addPayload);
        
        await addDeal(addPayload).unwrap();
        setSnackbar({
          open: true,
          message: 'Deal added successfully!',
          severity: 'success'
        });
      }
      
      handleBackToList();
      refetch();
    } catch (err: any) {
      console.error('Failed to save deal:', err);
      console.error('Error response:', err?.data);
      setSnackbar({
        open: true,
        message: err?.data?.message || `Failed to ${isEditing ? 'update' : 'add'} deal`,
        severity: 'error'
      });
    }
  };

  const handleDelete = async (deal: EnrichedDeal) => {
    const dealId = deal.deal_id;
    
    if (!dealId) {
      setSnackbar({
        open: true,
        message: 'Error: Deal ID is missing. Cannot delete.',
        severity: 'error'
      });
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete this deal? (ID: ${dealId})`)) {
      try {
        await deleteDeal(dealId).unwrap();
        setSnackbar({
          open: true,
          message: 'Deal deleted successfully!',
          severity: 'success'
        });
        refetch();
      } catch (err: any) {
        console.error('Failed to delete deal:', err);
        const errorMessage = err?.data?.message || err?.message || 'Failed to delete deal. Please try again.';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'AED') => {
    return new Intl.NumberFormat('ar-AE', {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'symbol'
    }).format(amount);
  };

  const getDisplayId = (deal: EnrichedDeal) => {
    return deal.deal_id || 'N/A';
  };

  const renderStatus = (deal: EnrichedDeal) => {
    const displayStatus = deal.display_status !== undefined ? deal.display_status : getDisplayStatus(deal);
    
    if (displayStatus === 1) {
      return (
        <Chip 
          label="Active" 
          size="small" 
          sx={{ 
            bgcolor: '#d1f4e0', 
            color: '#00783e', 
            fontWeight: 600, 
            fontSize: '12px' 
          }} 
        />
      );
    } else {
      // Determine why it's inactive for better UX
      const manualStatus = deal.deal_status;
      const currentStatus = deal.current_status;
      
      let label = 'Inactive';
      if (manualStatus === 0) {
        label = 'Inactive'; // Manually disabled
      } else if (currentStatus === 'upcoming') {
        label = 'Upcoming';
      } else if (currentStatus === 'expired') {
        label = 'Expired';
      }
      
      return (
        <Chip 
          label={label} 
          size="small" 
          sx={{ 
            bgcolor: '#e4e5e7', 
            color: '#616161', 
            fontWeight: 600, 
            fontSize: '12px' 
          }} 
        />
      );
    }
  };

  // Calculate counts for tabs
  const activeDealsCount = enrichedDeals.filter(deal => {
    const displayStatus = deal.display_status !== undefined ? deal.display_status : getDisplayStatus(deal);
    return displayStatus === 1;
  }).length;
  
  const inactiveDealsCount = enrichedDeals.filter(deal => {
    const displayStatus = deal.display_status !== undefined ? deal.display_status : getDisplayStatus(deal);
    return displayStatus === 0;
  }).length;

  const isLoadingData = isLoading || isLoadingAllProducts;

  // Generate time options for time select
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        times.push(`${hourStr}:${minuteStr}`);
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {!showForm ? (
              // LIST VIEW
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Deals of the Day
                  </Typography>
                  
                  <Button
                    variant="contained"
                    onClick={handleAddDeal}
                    sx={{
                      bgcolor: '#202223',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#303030' }
                    }}
                  >
                    Add deal
                  </Button>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs 
                      value={tabValue} 
                      onChange={handleTabChange}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '14px',
                          minHeight: 48,
                          color: '#6d7175',
                        },
                        '& .Mui-selected': {
                          color: '#202223',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#202223',
                          height: 3,
                        }
                      }}
                    >
                      <Tab label={`All (${enrichedDeals.length})`} />
                      <Tab label={`Active (${activeDealsCount})`} />
                      <Tab label={`Inactive (${inactiveDealsCount})`} />
                    </Tabs>
                  </Box>

                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search deals by product name, ID, discount, or price"
                      value={searchStr}
                      onChange={handleSearch}
                      fullWidth
                      sx={{ 
                        maxWidth: 400,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#c9cccf',
                        color: '#202223',
                        borderRadius: '8px',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#202223',
                          bgcolor: 'transparent',
                        }
                      }}
                    >
                      Filter
                    </Button>
                  </Box>

                  {isLoadingData && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoadingData && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load deals. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoadingData && !isError && paginatedDeals.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <LocalOffer sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        No deals found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first deal'}
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleAddDeal}
                        sx={{
                          bgcolor: '#202223',
                          color: 'white',
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '8px',
                          '&:hover': { bgcolor: '#303030' }
                        }}
                      >
                        Add deal
                      </Button>
                    </Box>
                  )}

                  {!isLoadingData && paginatedDeals.length > 0 && (
                    <Box sx={{ bgcolor: 'white' }}>
                      {/* Table Header */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '50px 100px 1fr 120px 100px 120px 180px 180px 100px 100px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          #
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Deal ID
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Product
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Original
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Discount
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Sale Price
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Start Date
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          End Date
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Status
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                          Actions
                        </Typography>
                      </Box>

                      {/* Table Rows */}
                      {paginatedDeals.map((deal: EnrichedDeal, index: number) => (
                        <Box
                          key={deal.deal_id || index}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '50px 100px 1fr 120px 100px 120px 180px 180px 100px 100px',
                            gap: 2,
                            px: 2,
                            py: 2,
                            alignItems: 'center',
                            borderBottom: index < paginatedDeals.length - 1 ? '1px solid #e1e3e5' : 'none',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#6d7175' }}>
                            {(page - 1) * dealsPerPage + index + 1}
                          </Typography>

                          <Chip 
                            label={getDisplayId(deal)} 
                            size="small"
                            sx={{ 
                              bgcolor: '#fff3e0',
                              color: '#e65100',
                              fontWeight: 600,
                              fontSize: '11px'
                            }}
                          />

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              src={deal.product_image || deal.product_main_image ? `https://fftestservice.sarsetech.com/images/${deal.product_image || deal.product_main_image}` : undefined}
                              variant="rounded"
                              sx={{ width: 40, height: 40, bgcolor: '#f1f2f4' }}
                            >
                              {(deal.product_name || deal.prod_name)?.[0] || 'P'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                {deal.product_name || deal.prod_name || 'Product'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {deal.product_id}
                              </Typography>
                            </Box>
                          </Box>

                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                            {formatCurrency(deal.original_price || 0, deal.currency)}
                          </Typography>

                          <Chip
                            label={`${deal.discount}%`}
                            size="small"
                            sx={{ 
                              bgcolor: '#fff3e0',
                              color: '#e65100',
                              fontWeight: 600
                            }}
                          />

                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                            {formatCurrency(deal.sale_price || 0, deal.currency)}
                          </Typography>

                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                            {formatDateTime(deal.start_date)}
                          </Typography>

                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                            {formatDateTime(deal.end_date)}
                          </Typography>

                          <Box>
                            {renderStatus(deal)}
                          </Box>

                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditDeal(deal)}
                              sx={{ 
                                color: '#6d7175',
                                '&:hover': { bgcolor: '#f1f2f4' }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(deal)}
                              disabled={isDeleting}
                              sx={{ 
                                color: '#6d7175',
                                '&:hover': { bgcolor: '#f1f2f4' }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Card>

                {totalPages > 1 && !isLoadingData && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handleChangePage}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          fontWeight: 500,
                        },
                        '& .Mui-selected': {
                          bgcolor: '#202223 !important',
                          color: 'white',
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              // FORM VIEW
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ 
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    Deals of the Day
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit deal' : 'Add deal'}
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Deal details
                            </Typography>
                            
                            <TextField
                              select
                              label="Select Product *"
                              value={formData.product_id}
                              onChange={(e) => {
                                const productId = e.target.value;
                                const product = allProducts.find((p: Product) => 
                                  (p.product_id?.toString() === productId || p.id?.toString() === productId)
                                );
                                setSelectedProduct(product || null);
                                setFormData(prev => ({
                                  ...prev,
                                  product_id: productId
                                }));
                                if (formErrors.product_id) {
                                  setFormErrors(prev => ({
                                    ...prev,
                                    product_id: undefined
                                  }));
                                }
                              }}
                              error={!!formErrors.product_id}
                              helperText={formErrors.product_id}
                              disabled={isEditing}
                              fullWidth
                            >
                              <MenuItem value="">
                                <em>Select a product</em>
                              </MenuItem>
                              {allProducts.map((product: Product) => (
                                <MenuItem 
                                  key={product.product_id || product.id} 
                                  value={(product.product_id || product.id).toString()}
                                >
                                  {product.prod_name} (ID: {product.product_id || product.id})
                                </MenuItem>
                              ))}
                            </TextField>

                            {selectedProduct && (
                              <Box sx={{ p: 2, bgcolor: '#fafbfb', borderRadius: '8px', mt: 2, border: '1px solid #e1e3e5' }}>
                                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                                  SELECTED PRODUCT:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                  <Avatar
                                    src={selectedProduct.product_main_image ? `https://fftestservice.sarsetech.com/images/${selectedProduct.product_main_image}` : undefined}
                                    variant="rounded"
                                    sx={{ width: 60, height: 60 }}
                                  >
                                    {selectedProduct.prod_short_name?.[0] || selectedProduct.prod_name?.[0] || 'P'}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                      {selectedProduct.prod_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Original Price: {formatCurrency(selectedProduct.product_price || 0)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}

                            <TextField
                              label="Discount (%) *"
                              name="discount"
                              type="number"
                              fullWidth
                              value={formData.discount}
                              onChange={handleInputChange}
                              error={!!formErrors.discount}
                              helperText={formErrors.discount || 'Enter discount percentage (1-100)'}
                              sx={{ mt: 2 }}
                              InputProps={{
                                inputProps: { 
                                  min: 1, 
                                  max: 100, 
                                  step: 1 
                                }
                              }}
                            />

                            {selectedProduct && formData.discount && parseFloat(formData.discount) > 0 && (
                              <Box sx={{ p: 2, bgcolor: '#f1f8e9', borderRadius: '8px', mt: 2, border: '1px solid #c8e6c9' }}>
                                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                                  PRICE PREVIEW:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Original Price:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {formatCurrency(selectedProduct.product_price || 0)}
                                    </Typography>
                                  </Box>
                                  <Typography variant="h6" color="text.secondary">→</Typography>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Sale Price:
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                      {formatCurrency(
                                        (selectedProduct.product_price || 0) - 
                                        ((selectedProduct.product_price || 0) * parseFloat(formData.discount) / 100)
                                      )}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Card>

                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Date & Time
                            </Typography>
                            
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Start Date *"
                                  name="start_date"
                                  type="date"
                                  fullWidth
                                  value={formData.start_date}
                                  onChange={handleInputChange}
                                  error={!!formErrors.start_date}
                                  helperText={formErrors.start_date}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  inputProps={{
                                    max: formData.end_date || '2100-12-31'
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  select
                                  label="Start Time"
                                  name="start_time"
                                  value={formData.start_time}
                                  onChange={handleInputChange}
                                  fullWidth
                                >
                                  {timeOptions.map((time) => (
                                    <MenuItem key={time} value={time}>
                                      {time}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <FormHelperText>Default: 00:00 (Start of day)</FormHelperText>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="End Date *"
                                  name="end_date"
                                  type="date"
                                  fullWidth
                                  value={formData.end_date}
                                  onChange={handleInputChange}
                                  error={!!formErrors.end_date}
                                  helperText={formErrors.end_date}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  inputProps={{
                                    min: formData.start_date || '1900-01-01'
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  select
                                  label="End Time"
                                  name="end_time"
                                  value={formData.end_time}
                                  onChange={handleInputChange}
                                  fullWidth
                                >
                                  {timeOptions.map((time) => (
                                    <MenuItem key={time} value={time}>
                                      {time}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <FormHelperText>Default: 23:59 (End of day)</FormHelperText>
                              </Grid>
                            </Grid>
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                        <Box sx={{ p: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                            Status
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={formData.status === 1}
                                onChange={handleStatusChange}
                                color="primary"
                              />
                            }
                            label={formData.status === 1 ? "Active" : "Inactive"}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {formData.status === 1 
                              ? "Deal will be visible when within date range" 
                              : "Deal will be hidden from users"}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    justifyContent: 'flex-end', 
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #e1e3e5'
                  }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isSaving}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#202223',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSaving || !selectedProduct || !formData.discount || !formData.start_date || !formData.end_date}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      {isSaving ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          {isEditing ? 'Updating...' : 'Adding...'}
                        </Box>
                      ) : (
                        isEditing ? 'Update deal' : 'Add deal'
                      )}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DealOfTheDay;